import { Form, Input, Spin } from "antd";
import { pipe, TaskEither } from "fp";
import React from "react";
import styled from "styled-components";

import { Button } from "~/ButtonV2";
import * as Driver from "~/Driver";
import * as Log from "~/Log";
import { Remote, type Remote as RemoteOf } from "~/Remote";
import { Request } from "~/Request";
import { Time } from "~/Time";
import * as Typography from "~/Typography";
import { WebAPI } from "~/WebAPI";

import DriverCertification = ELDSubmit.Query.DriverCertification;

/**
 * Buttons sit at the end of a flex row rather than in a fixed grid column.
 * These footers held an antd `Col` of `span={3}` — an eighth of the panel,
 * narrower than the word "Cancel" — so the button overflowed its own column and
 * ran off the right edge. Nothing here has a width worth fixing in advance.
 */
const footerStyle = {
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
  marginTop: "16px",
} as const;

export const Container = styled.div`
  .cancel-btn {
    margin-right: 16px;
  }
  .ant-form-item-control-input-content {
    align-self: flex-end;
  }
  form {
    padding: 4px;
  }
`;

/**
 * What the certification check said, in the terms the menu needs to title its
 * popover. `null` while the answer is still outstanding, or when there is no
 * driver to ask about.
 */
export type CertificationState =
  | "certified"
  | "failed"
  | "spans-migration"
  | "unavailable"
  | null;

type ELDSubmitRoot = {
  readonly driver?: Driver.Driver;
  readonly interval: Log.Summaries.Identity["interval"];
  readonly onCloseELDSubmitForm: (notes: string | undefined) => void;
  readonly onCloseELDError: () => void;
  readonly setCertification: (state: CertificationState) => void;
  /**
   * Narrow the submission to one of the periods the API named, and check that
   * period on its own. Each part goes back through certification in full — the
   * split answers where the driver's history divides, not whether either half
   * can actually be filed.
   */
  readonly onSubmitPeriod: (
    range: DriverCertification.CertificationDateRange
  ) => void;
};

export function ELDSubmitRoot({
  driver,
  interval,
  onCloseELDSubmitForm,
  onCloseELDError,
  setCertification,
  onSubmitPeriod,
}: ELDSubmitRoot) {
  // Reached through its full path rather than the alias above: the alias is
  // bound while this module's body runs, which is before the namespace holding
  // it exists, so using it as a value there resolves to undefined.
  const certification = ELDSubmit.Query.DriverCertification.use({
    driver,
    interval,
  });

  const state: CertificationState = driver
    ? pipe(
        certification,
        Remote.when<DriverCertification.EldCertifiedStatus, CertificationState>({
          idle: () => null,
          loading: () => null,
          error: () => "unavailable",
          success: (status) =>
            status.isEldCertified
              ? "certified"
              : spansMigration(status)
              ? "spans-migration"
              : "failed",
        })
      )
    : null;

  // Reported to the menu in an effect rather than during render: setting a
  // parent's state while a child renders is a React error, and this used to be
  // one.
  React.useEffect(() => setCertification(state), [state, setCertification]);

  if (!driver) {
    return null;
  }

  return pipe(
    certification,
    Remote.when<DriverCertification.EldCertifiedStatus, JSX.Element>({
      idle: () => <Checking />,
      loading: () => <Checking />,
      // The check is what stands between an admin and a filing that cannot
      // succeed, so a check that did not run is not a reason to let the
      // submission through — nor to sit on an empty popover, which is what
      // this did before and read as the menu being broken.
      error: () => <ELDCertificationUnavailable onClose={onCloseELDError} />,
      success: (status) =>
        status.isEldCertified ? (
          <ELDSubmit onClose={onCloseELDSubmitForm} />
        ) : (
          <ELDCertificationError
            driver={driver}
            certifiedStatus={status}
            onClose={onCloseELDError}
            onSubmitPeriod={onSubmitPeriod}
          />
        ),
    })
  );
}

// A `div`, not a `p`: antd's `Spin` renders a `div`, which a `p` may not hold.
const Checking = () => (
  <Container className="certification-popup">
    <div style={{ alignItems: "center", display: "flex", gap: "8px" }}>
      <Spin size="small" />
      <span>Checking whether these logs can be submitted…</span>
    </div>
  </Container>
);

/**
 * The certification check itself failed — a network fault, an expired session,
 * a driver this account cannot see. Distinct from a driver who failed the
 * check: nothing was decided, so the honest thing is to say so and let them
 * try again rather than imply either answer.
 */
export function ELDCertificationUnavailable({
  onClose,
}: {
  readonly onClose: () => void;
}) {
  return (
    <Container className="certification-popup">
      <p>
        <Typography.Component strong>
          {"We couldn't check whether these logs can be submitted."}
        </Typography.Component>
      </p>
      <p>
        The check did not complete, so nothing has been submitted. Try again in
        a moment, and contact Support if it keeps happening.
      </p>
      <div style={footerStyle}>
        <Button
          type="primary"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Container>
  );
}

export function ELDSubmit({
  onClose,
}: {
  readonly onClose: (notes: string | undefined) => void;
}) {
  const [formRef] = Form.useForm();

  const onCancel = () => {
    onClose(undefined);
  };

  const onSubmit = (notes: string) => {
    onClose(notes);
  };

  return (
    <Container>
      <Form
        form={formRef}
        {...{
          labelCol: {
            span: 24,
          },
          wrapperCol: {
            span: 24,
          },
        }}
        autoComplete="off"
      >
        <p>
          <Typography.Component muted small>
            You may assign a reference identifier or comment to your E-RODs
            submission below.
          </Typography.Component>
        </p>
        <Form.Item label="Reference note:" name="notes" shouldUpdate>
          <Input.TextArea size="small" rows={2} />
        </Form.Item>

        <Form.Item
          {...{
            wrapperCol: {
              offset: 16,
              span: 8,
            },
          }}
        >
          <Button className="cancel-btn" onClick={() => onCancel()}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            onClick={() => onSubmit(formRef.getFieldValue("notes") ?? "")}
          >
            Submit
          </Button>
        </Form.Item>
      </Form>
    </Container>
  );
}

/**
 * The failure raised when the requested period spans the driver's migration to
 * the new ELD system (`SPANS_MIGRATION`, id 8).
 *
 * Unlike every other failure in this list it says nothing is wrong with the
 * driver or their vehicles — it is a property of the period that was asked
 * for. Their records are fine, and the same request submitted as two periods
 * succeeds, so it gets its own screen rather than being listed under
 * "submissions have been disabled for this driver".
 *
 * Showing only that screen loses nothing: the API short-circuits on this case
 * and returns it as the sole failure, before any vehicle check has run. It has
 * to — with the period split across two stacks there is no single vehicle
 * list, so any asset ids alongside it would describe a different period than
 * the one asked about.
 */
const SPANS_MIGRATION = 8;

const spansMigration = (
  certifiedStatus: DriverCertification.EldCertifiedStatus
): boolean =>
  certifiedStatus.eldCertificationFailureTypes.some(
    ({ eldCertificationFailureTypeId }) =>
      eldCertificationFailureTypeId === SPANS_MIGRATION
  );

export function ELDCertificationError({
  driver,
  certifiedStatus,
  onClose,
  onSubmitPeriod,
}: {
  readonly driver?: Driver.Driver;
  readonly certifiedStatus: DriverCertification.EldCertifiedStatus;
  readonly onClose: () => void;
  readonly onSubmitPeriod: (
    range: DriverCertification.CertificationDateRange
  ) => void;
}) {
  if (spansMigration(certifiedStatus)) {
    return (
      <ELDSpansMigrationError
        driver={driver}
        suggestedRanges={certifiedStatus.suggestedRanges}
        onClose={onClose}
        onSubmitPeriod={onSubmitPeriod}
      />
    );
  }

  return (
    <Container className="certification-popup">
      <p>
        ELD submissions have been disabled for this driver due to the following
        issues:
      </p>
      <ul>
        {certifiedStatus.eldCertificationFailureTypes.map((failureType) => (
          <li key={failureType.eldCertificationFailureTypeId}>
            {failureType.description}
          </li>
        ))}
      </ul>

      {certifiedStatus.missingVinAssetIds.length > 0 ? (
        <p>
          {`Missing VINs for asset(s): ${certifiedStatus.missingVinAssetIds.join(
            ", "
          )}`}
        </p>
      ) : null}

      {certifiedStatus.invalidVinAssetIds.length > 0 ? (
        <p>
          {" "}
          {`Invalid VINs for asset(s): ${certifiedStatus.invalidVinAssetIds.join(
            ", "
          )}`}{" "}
        </p>
      ) : null}

      {certifiedStatus.missingDotNumberAssetIds.length > 0 ? (
        <p>{`Missing DOT number for asset(s): ${certifiedStatus.missingDotNumberAssetIds.join(
          ", "
        )}`}</p>
      ) : null}

      {certifiedStatus.invalidDotNumberAssetIds.length > 0 ? (
        <p>{`Invalid DOT number for asset(s): ${certifiedStatus.invalidDotNumberAssetIds.join(
          ", "
        )}`}</p>
      ) : null}

      <div style={footerStyle}>
        <Button
          type="primary"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </Container>
  );
}

// Styled inline rather than with a `styled` component on purpose: adding one
// shifts styled-components' generated class names, which several unrelated
// suites capture in inline snapshots.
const periodListStyle = { listStyle: "none", margin: 0, padding: 0 } as const;

/**
 * One period per row, full width, rather than a date beside a button. A date
 * range and a button labelled with that same range do not fit on one line at
 * popover widths: the range wraps mid-date, which is both ugly and briefly
 * unreadable.
 */
const periodRowStyle = { marginBottom: "8px" } as const;

/** Wide enough that a full range fits on one line at the sizes antd picks. */
const periodPanelStyle = { minWidth: "340px" } as const;

/**
 * "Driver was not paired during time period" — which, despite the wording, is
 * a statement about the period rather than about the driver. Both stacks raise
 * it from the same place, and both mean "this period yielded no vehicle", so on
 * a period holding nothing at all it is the failure that comes back.
 *
 * The split the API suggests is geometric: it slices the request at the day the
 * driver's history moves stacks, without checking that either side holds
 * anything. A period beginning well before a driver's first legacy record
 * therefore splits into one half that can be filed and one that is simply
 * empty.
 */
const NOT_PAIRED = 4;

type PeriodOutcome =
  | { readonly kind: "checking" }
  | { readonly kind: "submittable" }
  | { readonly kind: "empty" }
  | { readonly kind: "blocked"; readonly reasons: readonly string[] }
  | { readonly kind: "unavailable" };

/**
 * What the asset-id lists say on their own.
 *
 * The API populates these alongside a matching failure type, so normally they
 * add nothing. They are read anyway because the consequence of trusting only
 * the failure list is asymmetric: a period carrying asset ids but no failure
 * type would be classed as empty and dropped from the ones offered, losing a
 * period the admin still has to file. Reading them can at worst show a reason
 * twice; ignoring them can hide a period entirely.
 *
 * Each list names its own problem, so the wording is derived rather than
 * invented.
 */
const toAssetReasons = (
  status: DriverCertification.EldCertifiedStatus
): readonly string[] =>
  [
    [status.missingVinAssetIds, "Missing vehicle VIN"] as const,
    [status.invalidVinAssetIds, "Invalid vehicle VIN"] as const,
    [status.missingDotNumberAssetIds, "Missing vehicle DOT number"] as const,
    [status.invalidDotNumberAssetIds, "Invalid vehicle DOT number"] as const,
  ]
    .filter(([ids]) => ids.length > 0)
    .map(([, description]) => description);

/**
 * Empty only when nothing but {@link NOT_PAIRED} came back, and no asset was
 * named. A period can be both empty and carry driver-level failures — a
 * missing license number holds for every period — and those are worth showing
 * rather than hiding behind "no logs here", since they block the other half
 * too.
 */
const toOutcome = (
  certification: RemoteOf<DriverCertification.EldCertifiedStatus>
): PeriodOutcome =>
  pipe(
    certification,
    Remote.when<DriverCertification.EldCertifiedStatus, PeriodOutcome>({
      idle: () => ({ kind: "checking" }),
      loading: () => ({ kind: "checking" }),
      error: () => ({ kind: "unavailable" }),
      success: (status) => {
        if (status.isEldCertified) {
          return { kind: "submittable" };
        }

        const reported = status.eldCertificationFailureTypes
          .filter(
            ({ eldCertificationFailureTypeId }) =>
              eldCertificationFailureTypeId !== NOT_PAIRED
          )
          .map(({ description }) => description);

        // Only where the failure list said nothing, so a problem the API
        // described is not repeated in the API's words and then again in ours.
        const reasons =
          reported.length > 0 ? reported : toAssetReasons(status);

        return reasons.length === 0
          ? { kind: "empty" }
          : { kind: "blocked", reasons };
      },
    })
  );

const describeRange = (
  range: DriverCertification.CertificationDateRange
): string =>
  `${Time.DateTime.toDateOnlyMMMDYYYY(
    range.start
  )} – ${Time.DateTime.toDateOnlyMMMDYYYY(range.stop)}`;

/**
 * Shown when the requested period spans the driver's migration between the
 * previous and current ELD systems. One file cannot cover both, so the request
 * is refused and the periods that together cover it — supplied by the API, not
 * derived here — are offered to be submitted one at a time.
 *
 * Each is checked here, before any of them is offered, because the API's split
 * is geometric and does not know whether either side holds anything. Without
 * that, an admin whose range starts before the driver's first legacy record is
 * told to file two periods, one of which can never succeed — and only finds out
 * by trying it, under a heading blaming the driver's records.
 *
 * Deliberately one period at a time rather than a single "submit both" action.
 * Each filing is irreversible, and sending them together would have to report a
 * half that filed alongside a half that was refused, which is the shape of
 * result an admin is most likely to read as "done".
 */
export function ELDSpansMigrationError({
  driver,
  suggestedRanges,
  onClose,
  onSubmitPeriod,
}: {
  readonly driver?: Driver.Driver;
  readonly suggestedRanges: DriverCertification.EldCertifiedStatus["suggestedRanges"];
  readonly onClose: () => void;
  readonly onSubmitPeriod: (
    range: DriverCertification.CertificationDateRange
  ) => void;
}) {
  const checks = ELDSubmit.Query.DriverCertification.useAll(
    suggestedRanges.map((interval) => ({ driver, interval }))
  );

  const outcomes = checks.map(toOutcome);
  const period = (index: number): PeriodOutcome =>
    outcomes[index] ?? { kind: "checking" };

  const submittable = suggestedRanges.filter(
    (_, index) => period(index).kind === "submittable"
  );
  const settled = outcomes.every(({ kind }) => kind !== "checking");
  const nothingAnywhere =
    settled &&
    outcomes.length > 0 &&
    outcomes.every(({ kind }) => kind === "empty");

  /**
   * Whether the screen may say the driver's records are fine.
   *
   * Requires every period to have answered, not merely for none of them to
   * have failed yet: until a check settles nothing is known about it, and an
   * outstanding one is not evidence of a clean record. Without `settled` this
   * reads true on the first render — every period is still `checking`, so
   * nothing is `blocked` or `unavailable` — and the sentence appears, then
   * retracts itself when a period comes back with a real problem.
   *
   * `outcomes.length > 0` for the other end of the same argument. When the API
   * refuses for this reason but names no periods, there is nothing to check and
   * both tests above pass vacuously — `[].every()` is `true`, `[].some()` is
   * `false` — so the claim would be made having verified nothing at all. Never
   * known is no better than not yet known.
   */
  const recordsAreFine =
    settled &&
    outcomes.length > 0 &&
    !outcomes.some(
      ({ kind }) => kind === "blocked" || kind === "unavailable"
    );

  // The case this check exists for: the range reaches back before anything the
  // previous system holds, so only the later half can be filed. Said plainly and
  // up front, because the alternative is an admin opening a file that covers
  // less than the range they picked and having no idea why.
  const onlyOneHasLogs =
    settled &&
    submittable.length === 1 &&
    outcomes.every(({ kind }) => kind === "submittable" || kind === "empty");

  if (nothingAnywhere) {
    return (
      <Container className="certification-popup" style={periodPanelStyle}>
        <p>
          <Typography.Component strong>
            There are no logs to submit for this period.
          </Typography.Component>
        </p>
        <p>
          Neither system holds any records for the range selected, so there is
          nothing an output file could cover.
        </p>
        <div style={footerStyle}>
          <Button
            type="primary"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </Container>
    );
  }

  if (onlyOneHasLogs) {
    const usable = submittable[0];
    const missing = suggestedRanges.filter(
      (_, index) => period(index).kind === "empty"
    );

    return (
      <Container className="certification-popup" style={periodPanelStyle}>
        <p>
          <Typography.Component strong>
            {`Logs only exist for ${describeRange(usable)}.`}
          </Typography.Component>
        </p>
        <p>
          {`There are no records for ${missing
            .map(describeRange)
            .join(" or ")}, so an output file for this driver can only cover ${describeRange(
            usable
          )} — not the full range selected.`}
        </p>
        <p>Submit that period?</p>
        <div style={footerStyle}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" onClick={() => onSubmitPeriod(usable)}>
            {`Submit ${describeRange(usable)}`}
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="certification-popup" style={periodPanelStyle}>
      <p>
        <Typography.Component strong>
          This period can&apos;t be submitted as a single file.
        </Typography.Component>
      </p>
      <p>
        These logs are split across the previous and current ELD systems, so one
        submission cannot cover the whole period.
        {recordsAreFine
          ? " The driver's records are fine — the period just has to be sent in two parts."
          : null}
      </p>

      {/* Absent only if the API refused for this reason without saying where
          the period divides. Naming no dates is better than guessing them. */}
      {suggestedRanges.length > 0 ? (
        <>
          <p>Submit each period separately:</p>
          <ul style={periodListStyle}>
            {suggestedRanges.map((range, index) => (
              <li
                key={`${range.start.dateTime}-${range.stop.dateTime}`}
                style={periodRowStyle}
              >
                <PeriodAction
                  outcome={period(index)}
                  range={range}
                  onSubmitPeriod={onSubmitPeriod}
                />
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>
          Narrow the date range so it falls entirely before or entirely after
          the driver&apos;s migration, then submit each part separately.
        </p>
      )}

      <div style={footerStyle}>
        <Button onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Container>
  );
}

/**
 * One period, as a whole row.
 *
 * A period that can be filed is *only* a button, labelled with its own dates —
 * the dates are not repeated beside it, which is what used to force the range
 * to wrap. A period that cannot be filed is a line of text naming the period
 * and why, rather than an action known in advance to fail.
 */
const PeriodAction = ({
  outcome,
  range,
  onSubmitPeriod,
}: {
  readonly outcome: PeriodOutcome;
  readonly range: DriverCertification.CertificationDateRange;
  readonly onSubmitPeriod: (
    range: DriverCertification.CertificationDateRange
  ) => void;
}) => {
  const unavailable = (reason: string) => (
    <Typography.Component muted small>
      {`${describeRange(range)} — ${reason}`}
    </Typography.Component>
  );

  switch (outcome.kind) {
    case "checking":
      return (
        <Typography.Component muted small>
          <Spin size="small" /> {describeRange(range)}
        </Typography.Component>
      );
    case "submittable":
      return (
        <Button block type="primary" onClick={() => onSubmitPeriod(range)}>
          {`Submit ${describeRange(range)}`}
        </Button>
      );
    case "empty":
      return unavailable("no logs recorded");
    case "blocked":
      return unavailable(outcome.reasons.join("; "));
    case "unavailable":
      return unavailable("couldn't check");
  }
};

export namespace ELDSubmit {
  export const use = () => Remote.Mutation.use(Query.request);

  export type ELDSubmitProps = Pick<Log.Summaries.Identity, "interval"> &
    Partial<Pick<Log.Summaries.Identity, "driver">> & {
      readonly comment: string;
    };

  export namespace Query {
    export const request = ({
      driver,
      interval,
      comment,
    }: ELDSubmitProps): Request.Fn<SubmitELDResponse> => (context) =>
      TaskEither.tryCatch(
        () =>
          WebAPI.clientFor(context)
            .mutate<
              WebAPI.ELD.SubmitEldOutputFileForDriverResult,
              WebAPI.ELD.SubmitEldOutputFileForDriverVariables
            >({
              mutation: WebAPI.ELD.SubmitEldOutputFileForDriverDocument,
              variables: {
                input: {
                  driverId: Number(driver?.id),
                  // Date-only, where the REST endpoint took timestamps: the API
                  // brackets the whole days itself.
                  //
                  // Read back in the same zone the day was bracketed in. The
                  // UTC form would name the day before for a submitter east of
                  // UTC and the day after for one west of it, so the window
                  // filed with the FMCSA would not be the one on screen.
                  startDate: Time.DateTime.toISOLocalWithoutTime(
                    Time.DateTime.toStartOfDay(interval.start)
                  ),
                  endDate: Time.DateTime.toISOLocalWithoutTime(
                    Time.DateTime.toEndOfDay(interval.stop)
                  ),
                  comment,
                  transmissionMethod: "SOAP",
                },
              },
            })
            .then(({ data }) => ({ ...toResponse(data), requested: interval })),
        WebAPI.ServiceError.toError
      );

    const toResponse = (
      data: WebAPI.ELD.SubmitEldOutputFileForDriverResult | null | undefined
    ): Omit<SubmitELDResponse, "requested"> => {
      const result = data?.submitEldOutputFileForDriver;

      // A resolved mutation carrying no result is not a submission. Reading it
      // as one would close the form and tell the submitter their logs are with
      // the FMCSA, which is the one wrong answer this flow must not give: they
      // would have no reason to try again, and nothing was filed.
      if (!result) {
        throw Error("submitEldOutputFileForDriver returned no result");
      }

      return {
        submissionId: result.submissionId,
        filed:
          result.startDate && result.endDate
            ? {
                start: Time.DateTime.fromISOUnsafe(result.startDate),
                stop: Time.DateTime.fromISOUnsafe(result.endDate),
              }
            : undefined,
      };
    };

    export type SubmitELDResponse = {
      readonly submissionId: string;
      /**
       * The window this submission asked for, carried back so the result can
       * be described against it.
       *
       * Not read from component state when the result arrives: the response
       * object is rebuilt on every render, so the effect that reports it fires
       * again after the form closes — by which point a submission narrowed to
       * one half of a split period would be compared against the page's full
       * range and reported as truncated when it was not.
       */
      readonly requested: Log.Summaries.Identity["interval"];
      /**
       * The window the file actually covers, which can be shorter than the one
       * requested — a migrated driver's history on the new stack begins shortly
       * before their rollout cutover, and an earlier start is truncated rather
       * than refused. Absent only if the API declined to report it.
       */
      readonly filed?: Log.Summaries.Identity["interval"];
    };

    export namespace DriverCertification {
      type Params = {
        readonly driver?: Driver.Driver;
        readonly interval: Log.Summaries.Identity["interval"];
      };

      export const key = ({ driver, interval }: Params) => [
        "ELDCertification",
        driver?.id,
        Time.DateTime.toISOLocalWithoutTime(interval.start),
        Time.DateTime.toISOLocalWithoutTime(interval.stop),
      ];

      // Called unconditionally so the hook order never changes, and held back
      // until there is a driver to ask about.
      export const use = (params: Params) =>
        Remote.Query.use(key(params), request(params), {
          enabled: params.driver !== undefined,
        });

      /**
       * Several periods at once, for deciding which of them are worth
       * offering before any is offered.
       *
       * Keyed identically to {@link use}, so checking a period here and
       * submitting it afterwards is one request rather than two.
       */
      export const useAll = (params: readonly Params[]) =>
        Remote.Query.useAll(
          params.map((each) => ({ key: key(each), request: request(each) })),
          { enabled: params.every(({ driver }) => driver !== undefined) }
        );

      export type CertificationFailureTypes = {
        readonly eldCertificationFailureTypeId: number;
        readonly description: string;
      };

      export type CertificationDateRange = {
        readonly start: Time.DateTime;
        readonly stop: Time.DateTime;
      };

      export type EldCertifiedStatus = {
        readonly isEldCertified: boolean;
        readonly eldCertificationFailureTypes: readonly CertificationFailureTypes[];
        readonly missingVinAssetIds: readonly number[];
        readonly invalidVinAssetIds: readonly number[];
        readonly missingDotNumberAssetIds: readonly number[];
        readonly invalidDotNumberAssetIds: readonly number[];
        /**
         * The periods to submit instead, when the requested period spans the
         * driver's migration to the new stack. Empty otherwise.
         */
        readonly suggestedRanges: readonly CertificationDateRange[];
      };

      export const request = ({
        driver,
        interval,
      }: Params): Request.Fn<EldCertifiedStatus> => (context) =>
        TaskEither.tryCatch(
          () =>
            WebAPI.clientFor(context)
              .query<
                WebAPI.ELD.VerifyEldCertificationResult,
                WebAPI.ELD.VerifyEldCertificationVariables
              >({
                query: WebAPI.ELD.VerifyEldCertificationDocument,
                variables: {
                  driverId: Number(driver?.id),
                  startDate: Time.DateTime.toISOLocalWithoutTime(
                    interval.start
                  ),
                  endDate: Time.DateTime.toISOLocalWithoutTime(interval.stop),
                },
                // The gate has to reflect the driver's certification right
                // now, not what it was the last time this period was checked.
                fetchPolicy: "network-only",
              })
              .then(({ data }) => toEldCertifiedStatus(data)),
          WebAPI.ServiceError.toError
        );

      const toEldCertifiedStatus = (
        data: WebAPI.ELD.VerifyEldCertificationResult | null | undefined
      ): EldCertifiedStatus => {
        const result = data?.verifyELDCertification;

        if (!result) {
          throw Error("verifyELDCertification returned no result");
        }

        // The one field with no safe default: `true` would let a submission
        // past the gate and `false` would block one without saying why, so an
        // answer that does not carry it is not an answer.
        if (typeof result.isEldCertified !== "boolean") {
          throw Error("verifyELDCertification returned no certification");
        }

        // An absent list is read as an empty one. It is the difference between
        // "no failures of this kind" and "the API declined to say", and for
        // every list here the two call for the same screen — whereas throwing
        // would replace a refusal the submitter can act on with a generic
        // "couldn't check".
        return {
          isEldCertified: result.isEldCertified,
          eldCertificationFailureTypes: result.eldCertificationFailureTypes ?? [],
          missingVinAssetIds: result.missingVinAssetIds ?? [],
          invalidVinAssetIds: result.invalidVinAssetIds ?? [],
          missingDotNumberAssetIds: result.missingDotNumberAssetIds ?? [],
          invalidDotNumberAssetIds: result.invalidDotNumberAssetIds ?? [],
          suggestedRanges: (result.suggestedRanges ?? []).map((range) => ({
            start: Time.DateTime.fromISOUnsafe(range.startDate),
            stop: Time.DateTime.fromISOUnsafe(range.endDate),
          })),
        };
      };
    }
  }
}
